import type { RenewalReminderState } from "@/hooks/subscription/useRenewalReminder";

interface RenewalPillProps {
  reminder: RenewalReminderState;
}

export function RenewalPill({ reminder }: RenewalPillProps) {
  const { timing } = reminder;
  if (!timing) return null;

  const { daysLeft } = timing;
  const isCritical = timing.stage !== "upcoming";
  const ringColor = isCritical ? "text-destructive" : "text-warning";
  const strokeColor = isCritical
    ? "hsl(var(--destructive))"
    : "hsl(var(--warning))";

  const circumference = 2 * Math.PI * 14;
  const strokeDashoffset = circumference - (daysLeft / 7) * circumference;

  return (
    <button
      type="button"
      onClick={reminder.openModal}
      aria-label={`FirstRep portal subscription ends in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}. Open renewal reminder`}
      className={`inline-flex min-h-10 max-w-full items-center gap-2 rounded-full border border-border px-2.5 py-1.5 shadow-sm transition-colors hover:bg-muted sm:min-h-11 sm:gap-3 sm:px-3 sm:py-2 ${
        isCritical ? "bg-destructive/10" : "bg-warning/10"
      }`}
    >
      <div className="relative h-7 w-7 shrink-0 sm:h-8 sm:w-8">
        <svg
          className="h-7 w-7 -rotate-90 sm:h-8 sm:w-8"
          viewBox="0 0 36 36"
          aria-hidden="true"
        >
          <circle
            cx="18"
            cy="18"
            r="14"
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth="3"
          />
          <circle
            cx="18"
            cy="18"
            r="14"
            fill="none"
            stroke={strokeColor}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-[8px] font-bold sm:text-[9px] ${ringColor}`}>
            {daysLeft}
          </span>
        </div>
      </div>
      <div className="min-w-0 flex-1 text-left leading-none sm:flex-none">
        <span
          className={`block whitespace-nowrap text-xs font-semibold sm:text-sm ${ringColor}`}
        >
          {daysLeft} day{daysLeft !== 1 ? "s" : ""} left
        </span>
        <span className="hidden pt-0.5 text-[11px] text-muted-foreground sm:block">
          on FirstRep portal
        </span>
      </div>
    </button>
  );
}
